import normalizeVoiceE164 from './voiceE164';

/**
 * Retailer-level SIP readiness reported by the Mercately proxy (`GET /api/v1/bsp/voice`).
 * When false the number is not configured/ready on voice-service and the browser
 * runtime must not raise sockets nor WebRTC/SIP registrations.
 *
 * When `options.appId` is set and the payload includes `lines[]`, readiness is
 * taken from that line only (fail closed if missing). Otherwise uses top-level
 * `sipEnabled` (legacy or aggregate multi-number).
 *
 * @param {unknown} payload
 * @param {{ appId?: string }} [options]
 * @returns {boolean}
 */
export default function normalizeVoiceSipEnabled(payload, options = {}) {
  const appId = options.appId ? String(options.appId) : null;
  const lines = Array.isArray(payload?.lines) ? payload.lines : null;

  if (appId && lines && lines.length > 0) {
    const line = lines.find(
      (entry) => String(entry?.appId ?? entry?.app_id ?? '') === appId
    );
    return line?.sipEnabled === true || line?.sip_enabled === true;
  }

  return payload?.sipEnabled === true || payload?.sip_enabled === true;
}

/**
 * @param {unknown} payload
 * @param {string} e164
 * @returns {{ appId: string|null, platformRetailerId: string|null, phoneNumber: string, name: string|null, sipEnabled: boolean }|null}
 */
export function findSipLineByPhone(payload, e164) {
  const target = normalizeVoiceE164(e164);
  if (!target) return null;

  const lines = Array.isArray(payload?.lines) ? payload.lines : null;
  if (!lines || lines.length === 0) return null;

  const line = lines.find((entry) => {
    const phone = entry?.phoneNumber ?? entry?.phone_number ?? '';
    return normalizeVoiceE164(String(phone || '')) === target;
  });
  if (!line) return null;

  const appId = line.appId ?? line.app_id ?? null;
  const platformRetailerId = line.platformRetailerId ?? line.platform_retailer_id ?? null;
  const nameRaw = line.name ?? line.displayName ?? line.display_name ?? null;
  const name = typeof nameRaw === 'string' && nameRaw.trim() ? nameRaw.trim() : null;

  return {
    appId: appId == null || appId === '' ? null : String(appId),
    platformRetailerId: platformRetailerId == null || platformRetailerId === ''
      ? null
      : String(platformRetailerId),
    phoneNumber: target,
    name,
    sipEnabled: line.sipEnabled === true || line.sip_enabled === true
  };
}

/**
 * Label for outbound line selector: `name (phone)` or phone alone.
 *
 * @param {{ name?: string|null, businessE164?: string, phoneNumber?: string }} line
 * @returns {string}
 */
export function formatOutboundLineLabel(line) {
  const phone = line?.businessE164 || line?.phoneNumber || '';
  const name = typeof line?.name === 'string' ? line.name.trim() : '';
  if (name && phone) return `${name} (${phone})`;
  return name || phone || '';
}

/**
 * Outbound gate: SIP readiness for a specific business E.164.
 * - Legacy (no lines): top-level sipEnabled.
 * - Multi (lines[]): match phoneNumber; missing line → fail closed.
 *
 * @param {unknown} payload
 * @param {string} e164
 * @returns {boolean}
 */
export function lineSipEnabledByPhone(payload, e164) {
  const target = normalizeVoiceE164(e164);
  if (!target) return false;

  const lines = Array.isArray(payload?.lines) ? payload.lines : null;
  if (!lines || lines.length === 0) {
    return payload?.sipEnabled === true || payload?.sip_enabled === true;
  }

  const line = findSipLineByPhone(payload, target);
  return line?.sipEnabled === true;
}

/**
 * Tenant outbound lines ∩ SIP-ready lines from aggregate BSP status.
 * Enriches each line with `appId` / `platformRetailerId` from the SIP payload when present.
 * Legacy (no `lines` in payload): keep all tenant lines iff top-level sipEnabled.
 * Fail closed when payload is missing.
 *
 * @param {Array<{ businessE164: string }>} tenantLines
 * @param {unknown} sipPayload
 * @returns {Array<{ businessE164: string, appId?: string|null, platformRetailerId?: string|null, name?: string|null }>}
 */
export function filterTenantLinesBySipStatus(tenantLines, sipPayload) {
  const lines = Array.isArray(tenantLines) ? tenantLines : [];
  if (lines.length === 0 || sipPayload == null) return [];

  return lines
    .filter((line) => lineSipEnabledByPhone(sipPayload, line?.businessE164))
    .map((line) => {
      const sipLine = findSipLineByPhone(sipPayload, line.businessE164);
      return {
        ...line,
        appId: sipLine?.appId ?? null,
        platformRetailerId: sipLine?.platformRetailerId ?? null,
        name: sipLine?.name ?? null
      };
    });
}
